/**
 * The four things a teacher works: After School, Enrichment, Chinese, Math.
 *
 * These are *navigation* categories, not a new data concept — every pillar resolves to
 * roster groups that already exist (see `rosterGroups.ts`). The portal previously had one
 * nav entry, "Attendance", and every choice a teacher actually makes happened inside the
 * page, in a tab strip that could run to eighteen entries. Promoting the four categories
 * to the menu makes the first choice the one a teacher already has in mind — "I am about
 * to run Chinese" — and leaves the in-page tabs for the choice *within* a category.
 *
 * Only pillars the teacher has a group for are shown, so a chess-only teacher gets one
 * entry rather than three empty ones.
 */

import { Backpack, Calculator, Languages, Sparkles } from 'lucide-react';
import type { ComponentType } from 'react';
import type { RosterGroup } from './rosterGroups';

export type PillarId = 'afterschool' | 'enrichment' | 'chinese' | 'math';

export interface Pillar {
  id: PillarId;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** The roster groups under this pillar, in the order `buildRosterGroups` produced them. */
  groups: RosterGroup[];
  /** Where the nav entry goes: this pillar's first group. */
  path: string;
}

interface PillarMeta {
  id: PillarId;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

/** Declaration order is menu order — the order a teacher's day runs. */
const PILLAR_META: readonly PillarMeta[] = [
  { id: 'afterschool', label: 'After School', icon: Backpack },
  { id: 'enrichment', label: 'Enrichment', icon: Sparkles },
  { id: 'chinese', label: 'Chinese', icon: Languages },
  { id: 'math', label: 'Math', icon: Calculator },
];

/** The four ids in menu order, for surfaces that group by pillar without building one. */
export const PILLAR_ORDER: readonly PillarId[] = PILLAR_META.map(p => p.id);

/**
 * Pillar labels, for surfaces outside the teacher portal that group by the same four
 * categories — the admin attendance history, for one. Derived from `PILLAR_META` so a rename
 * cannot leave the teacher's menu and an admin's filter disagreeing about what things are
 * called.
 */
export const PILLAR_LABEL: Record<PillarId, string> = PILLAR_META.reduce(
  (acc, meta) => ({ ...acc, [meta.id]: meta.label }),
  {} as Record<PillarId, string>,
);

/**
 * Which pillar a *program* belongs to, from its `programs.type` alone.
 *
 * The type rule stated once. `pillarOf` below asks the same question of a roster group,
 * whose `isAfterSchool` is this same `type === 'after-school'` test computed upstream in
 * `rosterGroups.ts`.
 *
 * Note what this means for camps: they are not `after-school`, so they land in Enrichment
 * along with chess and piano. That follows the fallback rule rather than contradicting it —
 * Enrichment is deliberately everything-else, so a new program type is categorised the day
 * it is created without anyone remembering to come here.
 *
 * A program can never be Chinese or Math: those two pillars are the fourteen virtual classes,
 * which have no `programs` row at all.
 */
export function pillarOfProgramType(programType: string | null | undefined): PillarId {
  return programType === 'after-school' ? 'afterschool' : 'enrichment';
}

/**
 * Which pillar a roster group belongs to.
 *
 * Enrichment is the fallback rather than an allowlist, the same way `attendanceModeFor`
 * treats an unfamiliar program type: a new enrichment class should appear the day it is
 * created, without anyone remembering to add it here.
 */
export function pillarOf(group: RosterGroup): PillarId {
  if (group.isAfterSchool) return 'afterschool';
  if (group.virtualSubject?.id === 'chinese') return 'chinese';
  if (group.virtualSubject?.id === 'math') return 'math';
  return 'enrichment';
}

export function pillarPath(group: RosterGroup): string {
  return `/staff-app/attendance/${group.id}`;
}

/** The pillars this teacher actually has rosters for today, in menu order. */
export function buildPillars(groups: readonly RosterGroup[]): Pillar[] {
  const byPillar = new Map<PillarId, RosterGroup[]>();
  for (const group of groups) {
    const id = pillarOf(group);
    const bucket = byPillar.get(id);
    if (bucket) bucket.push(group);
    else byPillar.set(id, [group]);
  }

  return PILLAR_META.flatMap(meta => {
    const pillarGroups = byPillar.get(meta.id);
    if (!pillarGroups || pillarGroups.length === 0) return [];
    return [{ ...meta, groups: pillarGroups, path: pillarPath(pillarGroups[0]) }];
  });
}

/**
 * The pillar a roster URL is currently inside, or null when it is not on a roster.
 *
 * Resolved by looking the group id up among the pillars rather than by parsing the path,
 * so an enrichment class — whose group id is a bare program UUID, carrying no hint of its
 * category — still lights up the right nav entry.
 */
export function activePillarId(
  pathname: string,
  pillars: readonly Pillar[],
): PillarId | null {
  const groupId = pathname.split('/')[3];
  if (!groupId) return null;
  return pillars.find(p => p.groups.some(g => g.id === groupId))?.id ?? null;
}

/**
 * The groups a teacher can tab between from where they are standing.
 *
 * Scoped to the current pillar, which is the whole point of promoting them: the tab strip
 * used to mix After School, Chinese, Math and every enrichment class into one row. After
 * School, Chinese and Math each hold exactly one group, so their strip disappears entirely
 * (`GroupSwitcher` hides below two) and Enrichment keeps a strip of just its own classes.
 */
export function groupsInSamePillar(
  groups: readonly RosterGroup[],
  current: RosterGroup | null,
): RosterGroup[] {
  if (!current) return [...groups];
  const id = pillarOf(current);
  return groups.filter(g => pillarOf(g) === id);
}
